import { Table } from '@acceso/design-system';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VideoPresencesSubTable } from '@/components/VideoPresences/components/VideoPresencesSubTable/VideoPresencesSubTable';
import { useVideoPresencesDatatable } from '@/components/VideoPresences/hooks/use-video-presences-datatable';
import { VideoPresencesManagementBar } from '@/components/VideoPresences/VideoPresencesManagementBar';
import {
  PeoplePresenceDropdown,
  useBroadcastScheduleContext,
} from '@/components/VideoPresencesInlineControls';
import { buildNewPeopleDataToSave } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/helpers/batchSelectionHelpers';
import {
  FrameActionType,
  TBroadcastScheduleOnUpdateInline,
  VISUALIZATION_OPTIONS_VALUES,
  VisualizationOption,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipPeople, TPostClipPeople } from '@/features/clips/types';
import { useGroupedPresences } from '@/hooks/use-grouped-presences';
import { useSegmentPresences } from '@/hooks/use-segment-presences';
import { PresenceTypeEnum } from '@/types/presences';

type ClipToolkitPeopleTableProps = {
  items: TClipPeople[];
  onPreview?: (item: TClipPeople, frame?: FrameActionType) => void;
  onUpdate?: (item: TClipPeople) => void;
  onUpdateInline?: TBroadcastScheduleOnUpdateInline<TPostClipPeople>;
  onDelete?: (item: TClipPeople, silently?: boolean) => void;
};

export function ClipToolkitPeopleTable({
  items,
  onPreview,
  onUpdate,
  onUpdateInline,
  onDelete,
}: ClipToolkitPeopleTableProps) {
  const { t } = useTranslation();
  const { personaClipData } = useBroadcastScheduleProgram();
  const { groupedPresences, groupVisualization } = useBroadcastScheduleContext();
  const { buildGroupOptions } = useSegmentPresences();
  const { getRowClassStyles, peopleById } = useGroupedPresences<TClipPeople>(
    items,
    onPreview,
    'people_id'
  );

  const renderEntityCell = useCallback(
    (item: TClipPeople, showSubArrows: (f: (v: boolean) => boolean) => void) => (
      <PeoplePresenceDropdown
        item={item}
        peopleById={peopleById}
        onPreview={onPreview}
        onUpdateInline={onUpdateInline}
        parentCallback={() => showSubArrows(prev => !prev)}
      />
    ),
    [onPreview, onUpdateInline, peopleById]
  );

  const {
    getPeopleOptions: getOptions,
    getPeopleColumns,
    getLogosThumbsColumns,
  } = useVideoPresencesDatatable({
    items,
    onPreview,
    onUpdateInline,
    onUpdate,
    onDelete,
    entityIdKey: 'people_id',
    showSupportColumn: false,
    buildDataToSave: buildNewPeopleDataToSave,
    presenceData: personaClipData ?? undefined,
    searchBarPlaceholder: t('Buscar por persona'),
    renderEntityCell,
  });

  const hasCustomExtraBar = onDelete && onUpdateInline && onPreview;

  const columnsByVisualization: Record<VisualizationOption, typeof getPeopleColumns> = {
    [VISUALIZATION_OPTIONS_VALUES.List]: getPeopleColumns,
    [VISUALIZATION_OPTIONS_VALUES.Thumbnails]: getLogosThumbsColumns,
  };

  const renderAdditionalRowForList = useCallback(
    (item: TClipPeople) => (
      <VideoPresencesSubTable
        item={item as TClipPeople & { children: TClipPeople[] }}
        columns={getPeopleColumns}
        onPreview={onPreview}
        onUpdate={onUpdate}
        onUpdateInline={onUpdateInline}
        onDelete={onDelete}
        showSupportColumn={false}
        renderEntityCell={child => (
          <PeoplePresenceDropdown
            item={child}
            peopleById={peopleById}
            onPreview={onPreview}
            onUpdateInline={onUpdateInline}
            hasParent
          />
        )}
        entityIdKey="people_id"
        buildDataToSave={buildNewPeopleDataToSave as any}
        presenceData={personaClipData ?? undefined}
        activeEntityId={personaClipData?.id}
        tableDataId="people-subtable"
      />
    ),
    [getPeopleColumns, onPreview, onUpdate, onUpdateInline, onDelete, personaClipData, peopleById]
  );

  const additionalRowByVisualization: Partial<
    Record<VisualizationOption, (item: TClipPeople) => React.ReactNode>
  > = {
    [VISUALIZATION_OPTIONS_VALUES.List]: renderAdditionalRowForList,
  };

  const groupOptions = useMemo(
    () => buildGroupOptions(PresenceTypeEnum.PEOPLE),
    [buildGroupOptions]
  );

  const customExtraBar = useMemo(
    () =>
      hasCustomExtraBar ? (
        <VideoPresencesManagementBar<TPostClipPeople>
          {...{ onDelete, onUpdateInline, items, onPreview }}
          groupOptions={groupOptions}
          presenceType={PresenceTypeEnum.PEOPLE}
        />
      ) : null,
    [hasCustomExtraBar, onDelete, onUpdateInline, items, onPreview, groupOptions]
  );

  if (!groupedPresences) return null;

  return (
    <Table<any>
      containerClassName="!overflow-x-hidden"
      className="broadcast-schedule-program-body-segment-toolkit-people-table !w-auto min-w-full table-fixed"
      options={getOptions}
      columns={columnsByVisualization[groupVisualization] ?? getPeopleColumns}
      data={groupedPresences}
      renderAdditionalRow={additionalRowByVisualization[groupVisualization]}
      customExtraBar={customExtraBar}
      ignoreWrap
      getSpecialClassNames={getRowClassStyles}
    />
  );
}
